import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import SplitType from "split-type";

gsap.registerPlugin(ScrollTrigger);

export const ANIMATION = {
  selectors: {
    animatedWords: ".js-animate-words",
    animatedParagraph: ".js-animate-paragraph",
    parallaxVertical: ".js-parallax-vertical",
    parallaxVerticalBottom: ".js-parallax-vertical-to-bottom",
    parallaxVerticalInnerImages: ".js-parallax-with-inner-images",
    outerParallaxVertical: ".js-outer-parallax-vertical",
    outmaskLeftToRight: ".js-animate-outmask-left-to-right",
    unmaskFromOuter: ".js-animate-unmask-from-outer",
    fixTop: ".js-fix-top",
    maskIn: ".js-animate-mask-in",
  },
};

/**
 * See if an element is on the screen to play a GSAP animation passed by parameter.
 *
 * @param {HTMLElement} elementToObserve - Element to observe
 * @param {Function} animation - Animation timeline to execute. Ex: () => appearances
 * @param {Number} threshold - % of the element must be visible to activate the intersection. Ex: 0.5 = 50%
 * @returns {void}
 */
export const animateObserver = ({ elementToObserve, animation, threshold = 0.5 }) => {
  const handleIntersection = (entries, observer) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        animation().play();
        observer.unobserve(entry.target);
      }
    });
  };

  const observer = new IntersectionObserver(handleIntersection, {
    root: null,
    rootMargin: "0px",
    threshold,
  });

  elementToObserve && observer.observe(elementToObserve);
};

//== TEXT ANIMATIONS ==//
export const animateWords = ({ classElement, options }) => {
  const title = SplitType.create(classElement, options);
  title.words.forEach((line) => {
    // I change the "line" property to "words" because of the "Composition content" component title
    line.classList.add("js-animation-line-overflow");
  });

  return gsap.fromTo(
    title.words,
    {
      y: 70,
      opacity: 0,
      duration: 2,
    },
    {
      y: 0,
      stagger: 0.1,
      opacity: 1,
    },
  );
};

//== FADE ANIMATIONS ==//
export const fadeIn = ({ classElement, duration }) => {
  return gsap.fromTo(
    classElement,
    {
      opacity: 0,
      duration: duration,
    },
    {
      opacity: 1,
      duration: duration,
    },
  );
};

export const fadeOut = ({ classElement, duration }) => {
  return gsap.fromTo(
    classElement,
    {
      opacity: 1,
    },
    {
      opacity: 0,
      duration: duration,
    },
  );
};

export const fadeInWithDelay = ({ classElement, duration, delay }) => {
  return gsap.fromTo(
    classElement,
    {
      opacity: 0,
    },
    {
      opacity: 1,
      duration: duration,
      delay: delay,
    },
  );
};
//== MASK ANIMATIONS ==//
export const unmaskOuter = ({ classElement }) => {
  return gsap.fromTo(
    classElement,
    {
      opacity: 0,
      transition: "clip-path 1s",
      clipPath: "polygon(6% 6%, 92% 6%, 92% 92%, 6% 92%)",
      webkitClipPath: "polygon(6% 6%, 92% 6%, 92% 92%, 6% 92%)",
    },
    {
      opacity: 1,
      duration: 0.5,
      clipPath: "polygon(0 0, 100% 0, 100% 100%, 0 100%)",
      ease: "power4.in",
      stagger: {
        each: 0.1,
      },
    },
    0,
  );
};

export const unmaskOuterLeft = ({ classElement }) => {
  return gsap.fromTo(
    classElement,
    {
      opacity: 0,
      clipPath: "polygon(0 0, 0% 0, 0% 100%, 0% 100%)",
      webkitClipPath: "polygon(0 0, 0% 0, 0% 100%, 0% 100%)",
    },
    {
      opacity: 1,
      duration: 1.5,
      clipPath: "polygon(0 0, 100% 0, 100% 100%, 0 100%)",
    },
  );
};

export const maskLeftToRight = ({ classElement }) => {
  return gsap.fromTo(
    classElement,
    {
      ease: "expo.inOut",
      clipPath: "polygon(30% 30%, 70% 30%, 70% 70%, 30% 70%)",
      webkitClipPath: "polygon(30% 30%, 70% 30%, 70% 70%, 30% 70%)",
      duration: 2.6,
    },
    {
      ease: "expo.inOut",
      clipPath: "polygon(0 0, 100% 0, 100% 100%, 0 100%)",
      webkitClipPath: "polygon(0 0, 100% 0, 100% 100%, 0% 100%)",
      duration: 2.6,
    },
  );
};

//== PARALLAX ANIMATIONS ==//
export const parallaxVerticalImages = ({ classElement, startsY, endsY, scale = 1.3 }) => {
  return gsap.fromTo(
    classElement,
    {
      y: startsY,
      scale: scale,
      ease: "none",
    },
    {
      y: endsY,
      scale: scale,
      ease: "none",
    },
  );
};

export const parallaxMask = ({ classElement, trigger }) => {
  gsap.fromTo(
    classElement,
    {
      y: "-15vw",
      scale: 1.5,
      ease: "none",
    },
    {
      scrollTrigger: {
        trigger: trigger,
        toggleActions: "restart reverse none none",
        start: "top 80%",
        end: "bottom 20%",
        scrub: true,
      },
      scale: 1.5,
      y: "15vw",
      ease: "none",
    },
  );
};

export const parallaxOpacity = ({ classElement, trigger }) => {
  gsap.fromTo(
    classElement,
    {
      opacity: 0,
      ease: "none",
    },
    {
      scrollTrigger: {
        trigger: trigger,
        toggleActions: "restart reverse none none",
        start: "top 80%",
        end: "50% center",
        scrub: true,
      },
      opacity: 1,
      ease: "none",
    },
  );
};

export const parallaxVertical = ({ classElement, startsY, endsY }) => {
  return gsap.fromTo(
    classElement,
    {
      y: startsY,
      ease: "none",
    },
    {
      y: endsY,
      ease: "none",
    },
  );
};

export const parallaxHorizontal = ({ classElement, trigger, start, end }) => {
  gsap
    .timeline({
      scrollTrigger: {
        id: "collage-scrollTrigger",
        trigger: trigger,
        toggleActions: "restart reverse none none",
        start: "top+=80 center",
        end: "bottom center",
        scrub: true,
      },
    })
    .fromTo(classElement, { x: start }, { x: end });
};

export const showHeroHome = (classElement) => {
  gsap.fromTo(
    classElement,
    {
      opacity: 0,
      scale: 1.2,
    },
    {
      opacity: 1,
      scale: 1,
      duration: 3,
      ease: "power1.inOut",
    },
  );
};

export const parallaxHeroHome = ({ classElement }) => {
  return gsap.to(classElement, {
    opacity: 0,
    background: "black",
    scale: 1.2,
    duration: 2,
    ease: "power1.inOut",
  });
};

export const opacityDelayBetweenItems = ({ classElement, duration }) => {
  return gsap.fromTo(
    classElement,
    {
      opacity: 0,
      duration: duration,
    },
    {
      opacity: 1,
      duration: duration,
      stagger: 0.5,
    },
  );
};

export const maskIn = ({ classElement, duration = 2.3 }) => {
  return gsap.fromTo(
    classElement,
    {
      opacity: 0,
      clipPath: "polygon(25% 30%, 75% 30%, 75% 70%, 25% 70%)",
      webkitClipPath: "polygon(25% 30%, 75% 30%, 75% 70%, 25% 70%)",
      ease: "expoScale(0.5,7,none)",
    },
    {
      opacity: 1,
      duration: duration,
      clipPath: "polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)",
      webkitClipPath: "polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)",
    },
  );
};

// This animation need to set a default value from SCSS class seting a default value to starts. EX: .brh-banner-showcase { clip-path: polygon(3% 5%, 98% 5%, 98% 98%, 3% 98%); }
export const maskInParallax = ({ classElement }) => {
  return gsap.to(classElement, {
    clipPath: "polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)",
    webkitClipPath: "polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)",
  });
};
